import type { Trofeu } from './tipos';

/**
 * Conquistas da conta (perfil do jogador). O catálogo é compartilhado: o servidor só concede ids que existem aqui e a tela
 * mostra as que ainda faltam como bloqueadas. Quem concede é o servidor (nunca o cliente), ao fim da temporada na sala online.
 */
export interface DefConquista {
  id: string;
  titulo: string;
  descricao: string;
  icone: string;
  /** como o servidor concede sozinho, partida a partida; sem regra, a conquista é dada por outro caminho (os títulos, no fim da temporada) */
  regra?: RegraConquista;
  /** WebCoins pagas junto com a conquista (0 ou ausente: nenhuma) */
  coins?: number;
  /** criada pelo painel admin (as do código não podem ser removidas) */
  personalizada?: boolean;
}

/**
 * Regras que o servidor confere a cada partida de um técnico com conta (só salas online):
 * - vitorias: total de vitórias numa competição (1 = "vença uma partida");
 * - sequencia: maior sequência de vitórias seguidas na competição.
 */
export type TipoRegra = 'vitorias' | 'sequencia';
export interface RegraConquista {
  tipo: TipoRegra;
  /** id de COMPETICOES_CONQUISTA ('todas' soma todas) */
  competicao: string;
  quantidade: number;
}

/** Competições em que as partidas são contadas. 'todas' reúne tudo (a sequência vale para qualquer jogo). */
export const COMPETICOES_CONQUISTA: readonly { id: string; nome: string }[] = [
  { id: 'todas', nome: 'qualquer competição' },
  { id: 'brasileirao', nome: 'Brasileirão' },
  { id: 'copa-do-brasil', nome: 'Copa do Brasil' },
  { id: 'libertadores', nome: 'Libertadores' },
  { id: 'sul-americana', nome: 'Sul-Americana' },
];

/** Contadores de uma conta numa competição. */
export interface EstatCompeticao {
  jogos: number;
  vitorias: number;
  empates: number;
  derrotas: number;
  /** vitórias seguidas agora */
  sequencia: number;
  melhorSequencia: number;
}
export type EstatConta = Record<string, EstatCompeticao>;

const estatVazia = (): EstatCompeticao => ({ jogos: 0, vitorias: 0, empates: 0, derrotas: 0, sequencia: 0, melhorSequencia: 0 });

/** Competição de uma partida: a liga conta como 'brasileirao' nas salas do Brasileirão; as outras ligas usam o id do campeonato. */
export function grupoCompeticao(campeonato: string, tipo: 'liga' | 'copa' | 'lib' | 'sul'): string {
  if (tipo === 'copa') return 'copa-do-brasil';
  if (tipo === 'lib') return 'libertadores';
  if (tipo === 'sul') return 'sul-americana';
  return /^brasileirao/.test(campeonato) ? 'brasileirao' : campeonato;
}

/** Soma uma partida nos contadores da competição e em 'todas'. */
export function registrarResultado(estat: EstatConta, competicao: string, r: 'V' | 'E' | 'D'): void {
  for (const k of new Set([competicao, 'todas'])) {
    const s = (estat[k] ??= estatVazia());
    s.jogos++;
    if (r === 'V') {
      s.vitorias++;
      s.sequencia++;
      s.melhorSequencia = Math.max(s.melhorSequencia, s.sequencia);
    } else {
      if (r === 'E') s.empates++;
      else s.derrotas++;
      s.sequencia = 0;
    }
  }
}

/** Quanto falta para a regra: `atual` já limitado ao alvo. */
export function progressoRegra(regra: RegraConquista, estat: EstatConta | undefined): { atual: number; alvo: number } {
  const s = estat?.[regra.competicao];
  const valor = !s ? 0 : regra.tipo === 'vitorias' ? s.vitorias : s.melhorSequencia;
  return { atual: Math.min(valor, regra.quantidade), alvo: regra.quantidade };
}

export const regraCumprida = (regra: RegraConquista, estat: EstatConta | undefined): boolean => {
  const p = progressoRegra(regra, estat);
  return p.atual >= p.alvo;
};

/** Valida uma regra vinda de fora (painel admin). */
export function validarRegra(x: unknown): RegraConquista {
  const r = x as Partial<RegraConquista> | null;
  if (!r || (r.tipo !== 'vitorias' && r.tipo !== 'sequencia')) throw new Error('regra inválida: tipo desconhecido');
  if (!COMPETICOES_CONQUISTA.some((c) => c.id === r.competicao)) throw new Error('regra inválida: competição desconhecida');
  if (!Number.isInteger(r.quantidade) || (r.quantidade as number) < 1 || (r.quantidade as number) > 10_000) throw new Error('regra inválida: quantidade');
  return { tipo: r.tipo, competicao: r.competicao as string, quantidade: r.quantidade as number };
}

/** Conquistas prontas do Brasileirão (o painel admin as cria; sem WebCoins até o dono do jogo definir as recompensas). */
export function conquistasDoBrasileirao(): DefConquista[] {
  const v = (n: number, titulo: string, descricao: string, icone: string): DefConquista => ({ id: `bra-vitorias-${n}`, titulo, descricao, icone, regra: { tipo: 'vitorias', competicao: 'brasileirao', quantidade: n }, personalizada: true });
  const s = (n: number, titulo: string, descricao: string, icone: string): DefConquista => ({ id: `bra-sequencia-${n}`, titulo, descricao, icone, regra: { tipo: 'sequencia', competicao: 'brasileirao', quantidade: n }, personalizada: true });
  return [
    v(1, 'Primeira vitória', 'Vença uma partida no Brasileirão.', '⚽'),
    v(10, 'Dez vitórias', 'Vença 10 partidas no Brasileirão.', '🔟'),
    v(25, 'Vitorioso', 'Vença 25 partidas no Brasileirão.', '💪'),
    v(50, 'Calejado', 'Vença 50 partidas no Brasileirão.', '🛡️'),
    s(3, 'Embalado', 'Vença 3 jogos seguidos no Brasileirão.', '🔥'),
    s(5, 'Cinco seguidas', 'Vença 5 jogos seguidos no Brasileirão.', '🚀'),
    s(10, 'Imparável', 'Vença 10 jogos seguidos no Brasileirão.', '👑'),
  ];
}

export const CATALOGO_CONQUISTAS: readonly DefConquista[] = [
  { id: 'campeao-serie-a', titulo: 'Campeão Brasileiro', descricao: 'Venceu o Brasileirão Série A.', icone: '🏆' },
  { id: 'campeao-serie-b', titulo: 'Campeão da Série B', descricao: 'Venceu o Brasileirão Série B.', icone: '🥇' },
  { id: 'campeao-copa-do-brasil', titulo: 'Campeão da Copa do Brasil', descricao: 'Venceu a Copa do Brasil.', icone: '🏅' },
  { id: 'campeao-libertadores', titulo: 'Campeão da Libertadores', descricao: 'Venceu a Copa Libertadores.', icone: '🌎' },
  { id: 'campeao-sul-americana', titulo: 'Campeão da Sul-Americana', descricao: 'Venceu a Copa Sul-Americana.', icone: '🌟' },
];

export const conquistaPorId = (id: string): DefConquista | undefined => CATALOGO_CONQUISTAS.find((c) => c.id === id);

/** Id da conquista que um troféu de campeão representa (ou null se o troféu não tem conquista). */
export function conquistaDoTrofeu(t: Pick<Trofeu, 'competicao'>): string | null {
  const nome = t.competicao;
  if (/sul-?americana/i.test(nome)) return 'campeao-sul-americana';
  if (/libertadores/i.test(nome)) return 'campeao-libertadores';
  if (/copa do brasil/i.test(nome)) return 'campeao-copa-do-brasil';
  if (/s[ée]rie a/i.test(nome)) return 'campeao-serie-a';
  if (/s[ée]rie b/i.test(nome)) return 'campeao-serie-b';
  return null;
}
